// Colours of the Journey Card. Cream (--color-background) and petrol from the visual identity, as on feedbackjourney.com.
export const CARD_BG = "#F6F1E8";
export const CARD_FG = "#0F2B3C";
const WHITE = "#FFFFFF";

// Ready-made colour pairs for the downloaded card. The first is the default. Amber is --color-accent, petrol --color-primary.
export const CARD_PRESETS = [
  { id: "cream", bg: CARD_BG, fg: CARD_FG, canvas: WHITE },
  { id: "white", bg: WHITE, fg: CARD_FG, canvas: CARD_BG },
  { id: "petrol", bg: CARD_FG, fg: CARD_BG, canvas: CARD_BG },
  { id: "amber", bg: "#E8B86A", fg: CARD_FG, canvas: CARD_FG },
] as const;

export type CardPreset = (typeof CARD_PRESETS)[number];

// The link colour, --color-link, for "Scan to verify" where it is readable on the card.
export const CARD_LINK = "#a3553a";

// The QR code has to stay dark on light, with WCAG AA contrast, to scan from screen and print.
export const MIN_QR_CONTRAST = 4.5;

const toRgb = (hexColor: string): [number, number, number] => {
  let hex = hexColor.startsWith("#") ? hexColor.slice(1) : hexColor;
  // Expand shorthand form (e.g. "03F") to full form ("0033FF")
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("");
  }
  return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
};

export function isColorDark(hexColor: string) {
  const [r, g, b] = toRgb(hexColor);
  // Brightness using the weighted formula, 128 as the threshold
  return (r * 299 + g * 587 + b * 114) / 1000 < 128;
}

const luminance = (hexColor: string) => {
  const [r, g, b] = toRgb(hexColor).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

// WCAG contrast ratio between two colours, from 1 to 21.
export function contrastRatio(a: string, b: string) {
  const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (light + 0.05) / (dark + 0.05);
}

// The card's own colours when they give a dark QR on light with enough contrast, otherwise petrol on cream.
export function qrColors(bg: string, fg: string) {
  if (luminance(fg) < luminance(bg) && contrastRatio(bg, fg) >= MIN_QR_CONTRAST) {
    return { bgColor: bg, fgColor: fg };
  }
  return { bgColor: CARD_BG, fgColor: CARD_FG };
}

// The caption under the QR code: the link colour when it has AA contrast on the card, otherwise the card's own colour.
export function captionColor(bg: string, fg: string) {
  return contrastRatio(bg, CARD_LINK) >= MIN_QR_CONTRAST ? CARD_LINK : fg;
}
